import { useState } from 'react';
import { Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { tooltipSx } from '../jernyTooltipStyling';
import JInspectorSelect from './JInspectorSelect';
import JInspectorSelectEdit from './JInspectorSelectEdit';

// Single row in JInspector — label + select dropdown, or readonly label + value, or text input.
// Props:
//   label      — display label string
//   value      — current selected value or display string
//   options    — array of { label, value } — omit for readonly or text
//   onChange   — fn(newValue) — omit for readonly
//   readonly   — render as text with copy icon, not select
//   text       — render as text input
//   textarea   — render as multi-line <textarea> instead of single-line <input> — use for
//                fields that can grow long (e.g. Remark)
//   tooltip    — optional tooltip text shown on the label
//   richSelect — render options via JInspectorSelect (typeahead dropdown, same visual style as
//                native <select>) instead of native <select>. Use when the option list can grow
//                large (e.g. user-defined types) — native <select> is fine for short, fixed lists.
//   editSelect — render via JInspectorSelectEdit (free-text combobox) instead of native <select>.
//                Use when the value isn't limited to the given options — e.g. Service, where you
//                need to type a brand-new, not-yet-existing service name to move an entry to it.
export default function JInspectorRow({
    label,
    value,
    options,
    onChange,
    readonly = false,
    text = false,
    textarea = false,
    tooltip,
    checkbox = false,
    richSelect = false,
    editSelect = false,
    onSave,
}) {
    const [copied, setCopied] = useState(false);

    const handleChange = (e) => {
        const parsed = Number(e.target.value);
        onChange(isNaN(parsed) ? e.target.value : parsed);
    };

    // Enter stays local (newline), Ctrl+Enter triggers save directly
    const handleTextareaKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.stopPropagation();
        if (e.ctrlKey) {
            e.preventDefault();
            onSave?.();
        }
    };

    const handleCopy = () => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    // Reusable label element — wraps in Tooltip if tooltip prop is provided
    const labelEl = tooltip ? (
        <Tooltip title={tooltip} placement="top" componentsProps={tooltipSx} PopperProps={{ style: { zIndex: 10000 } }}>
            <span className="j-tooltip-label" style={{ cursor: 'help' }}>
                {label}
            </span>
        </Tooltip>
    ) : (
        <span className="j-tooltip-label">{label}</span>
    );

    if (textarea) {
        return (
            <div className="j-inspector-row j-inspector-row--textarea">
                {labelEl}
                <textarea
                    className="j-inspector-textarea"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleTextareaKeyDown}
                    rows={3}
                />
            </div>
        );
    }

    if (text) {
        return (
            <div className="j-inspector-row">
                {labelEl}
                <input className="j-inspector-select" type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} />
            </div>
        );
    }

    if (checkbox) {
        return (
            <div className="j-inspector-row">
                <span />
                <label className="yns-checkbox-cell">
                    <span className="yns-checkbox-label">{label}</span>
                    <input
                        type="checkbox"
                        //
                        className="yns-checkbox"
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                </label>
            </div>
        );
    }

    if (readonly) {
        return (
            <div className="j-inspector-row">
                {labelEl}
                <span className="j-inspector-readonly-wrapper">
                    <span className="j-inspector-readonly">{value || '—'}</span>
                    {value && (
                        <button
                            className="j-inspector-copy-btn"
                            onClick={handleCopy}
                            title={copied ? 'Copied!' : 'Copy to clipboard'}
                            aria-label="Copy to clipboard"
                        >
                            {copied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
                        </button>
                    )}
                </span>
            </div>
        );
    }

    if (editSelect) {
        return (
            <div className="j-inspector-row">
                {labelEl}
                <JInspectorSelectEdit value={value ?? ''} onChange={onChange} options={options} />
            </div>
        );
    }

    if (richSelect) {
        return (
            <div className="j-inspector-row">
                {labelEl}
                <JInspectorSelect value={value ?? ''} onChange={onChange} options={options} defaultLabel="—" />
            </div>
        );
    }

    return (
        <div className="j-inspector-row">
            {labelEl}
            <select className="j-inspector-select" value={value ?? ''} onChange={handleChange}>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );
}
